import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, History } from 'lucide-react';
import { fetchTiledPeriodManifest } from '../../lib/tilePeriods';
import {
  OBSERVED_WEATHER_TILE_MIN_ZOOM,
  OBSERVED_WEATHER_UI_DEFS,
  latestTiledPeriod,
  type ObservedWeatherLayerKey,
} from '../../lib/observedWeather';
import { ClimateMonthPicker } from './ClimateMonthPicker';
import { WEATHER_MUTED } from './weatherDesign';
import { tourSegmentedBtn } from '../ui/TourStylePanelShell';

interface WeatherHistoricalClimateProps {
  activeLayer: ObservedWeatherLayerKey | null;
  selectedMonths: string[];
  onSelectLayer: (layer: ObservedWeatherLayerKey | null) => void;
  onToggleMonth: (period: string, enabled: boolean) => void;
}

const LAYER_OPTIONS: { key: ObservedWeatherLayerKey; label: string }[] = [
  { key: 'observedWeatherTemp', label: 'Temperature' },
  { key: 'observedWeatherWind', label: 'Wind' },
  { key: 'observedWeatherPrecip', label: 'Rainfall' },
];

export function WeatherHistoricalClimate({
  activeLayer,
  selectedMonths,
  onSelectLayer,
  onToggleMonth,
}: WeatherHistoricalClimateProps) {
  const [open, setOpen] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchTiledPeriodManifest(OBSERVED_WEATHER_UI_DEFS[0]!.tileLayer).then(({ periods }) => {
      if (cancelled) return;
      setAvailablePeriods(periods);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeLabel = useMemo(
    () => LAYER_OPTIONS.find((o) => o.key === activeLayer)?.label,
    [activeLayer],
  );

  const handleLayerClick = (key: ObservedWeatherLayerKey) => {
    if (activeLayer === key) {
      onSelectLayer(null);
      return;
    }
    onSelectLayer(key);
    if (selectedMonths.length === 0 && availablePeriods.length > 0) {
      const latest = latestTiledPeriod(availablePeriods);
      if (latest) onToggleMonth(latest, true);
    }
  };

  return (
    <div className="border-t border-gray-200/80 dark:border-gray-700/80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 sm:px-4"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          <History size={14} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span className="text-xs font-semibold text-gray-900 dark:text-white">
            Past weather patterns
          </span>
          {activeLabel && !open && (
            <span className="truncate text-xs font-normal text-emerald-600 dark:text-emerald-400">
              · {activeLabel}
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp size={16} className="shrink-0 text-gray-400" />
        ) : (
          <ChevronDown size={16} className="shrink-0 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="space-y-2.5 px-3 pb-3 sm:px-4">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Climate map layer">
            {LAYER_OPTIONS.map(({ key, label }) => {
              const selected = activeLayer === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleLayerClick(key)}
                  aria-pressed={selected}
                  className={
                    selected
                      ? tourSegmentedBtn(true)
                      : 'rounded-lg border border-gray-200/80 bg-white px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-800'
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>

          {activeLayer ? (
            <>
              <p className={`${WEATHER_MUTED} leading-snug`}>
                Typical {activeLabel?.toLowerCase()} for selected months — compare with this
                week&apos;s forecast. Zoom to level {OBSERVED_WEATHER_TILE_MIN_ZOOM}+ ·{' '}
                <span className="font-medium text-gray-600 dark:text-gray-300">Past years</span>{' '}
                legend on the map.
              </p>
              <ClimateMonthPicker
                options={availablePeriods}
                selected={selectedMonths}
                onToggle={onToggleMonth}
                emptyMessage="Past weather data is not on the map yet."
              />
            </>
          ) : (
            <p className={`${WEATHER_MUTED} leading-snug`}>
              Pick temperature, wind, or rainfall to shade the map with past-year normals.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
